import { Modal as ModalModule, UI, Utils } from 'common';
import React, { useContext, useState } from 'react';
import { User } from './hooks/useSubscriptionPlanState';
import PlanUpdatedSuccessModal from './PlanUpdatedSuccessModal.component';

const { Modal, ModalButtonActionType, ModalContext } = ModalModule;
const { InfoBanner, WrappedCheckbox, WrappedTooltip } = UI;
const { Session, Util } = Utils;

interface ReviewDeleteUsersModalProps {
	modalId: string;
	data: {
		users: User[];
		overflowCount: number;
		nextBillingDate: string;
		onDeleteUsers: (userIds: number[]) => Promise<void>;
	};
}

const ReviewDeleteUsersModal = ({
	modalId,
	data: { users, overflowCount, nextBillingDate, onDeleteUsers },
}: ReviewDeleteUsersModalProps) => {
	const { closeModal, showModal, showError } = useContext(ModalContext) as any;
	const { userId: currentUserId } = Session.getAccountInfo();

	const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(
		new Set()
	);
	const [isDeleting, setIsDeleting] = useState(false);

	const quotaMet = selectedUserIds.size >= overflowCount;

	const toggleUser = (userId: number) => {
		setSelectedUserIds(prev => {
			const next = new Set(prev);
			if (next.has(userId)) {
				next.delete(userId);
			} else {
				next.add(userId);
			}
			return next;
		});
	};

	const handleDelete = async () => {
		setIsDeleting(true);
		try {
			await onDeleteUsers(Array.from(selectedUserIds));
			closeModal(modalId);
			showModal({ component: PlanUpdatedSuccessModal });
		} catch (error) {
			setIsDeleting(false);
			showError({
				response: {
					message:
						error instanceof Error ? error.message : 'Failed to remove users.',
				},
			});
		}
	};

	const cancelButton = {
		type: ModalButtonActionType.Cancel,
		text: 'Cancel',
		dataqa: 'review-delete-users-cancel',
	};

	const confirmButton = {
		type: ModalButtonActionType.Confirm,
		text: isDeleting ? 'Updating...' : 'Update Plan',
		onClick: handleDelete,
		disabled: !quotaMet || isDeleting,
		loading: isDeleting,
		dataqa: 'review-delete-users-confirm',
	};

	return (
		<Modal
			modalId={modalId}
			header="Remove Users to Update Plan"
			primaryButton={confirmButton}
			secondaryButton={cancelButton}
			size="medium"
			className="review-delete-users-modal"
		>
			<div className="review-delete-users">
				<div className="review-delete-users__instruction">
					You’re currently using more seats than your selection. Once you’ve
					reduced your active users to the maximum allowed, you may be able to
					confirm your plan change. Select at least {overflowCount} active user
					{overflowCount !== 1 ? 's' : ''} to remove.
				</div>

				<div className="review-delete-users__list">
					{users.map(user => {
						const isSelf = user.UserId === currentUserId;
						const isChecked = selectedUserIds.has(user.UserId);

						const tooltipTarget = `review-delete-user-tooltip-${user.UserId}`;

						return (
							<div
								key={user.UserId}
								className={`review-delete-users__item${isSelf ? ' review-delete-users__item--disabled' : ''}`}
								onClick={() => !isSelf && toggleUser(user.UserId)}
								role="button"
								tabIndex={0}
							>
								<span className={isSelf ? tooltipTarget : undefined}>
									{isSelf && (
										<WrappedTooltip
											target={tooltipTarget}
											tooltip="You cannot remove your own account"
											position="bottom"
										/>
									)}
									<WrappedCheckbox
										checked={isChecked}
										disabled={isSelf}
										dataqa={`review-delete-user-${user.UserId}`}
									/>
								</span>
								<div className="review-delete-users__user-info">
									<div className="review-delete-users__user-row">
										<span className="review-delete-users__user-name">
											{user.FirstName} {user.LastName}
										</span>
										{user.RoleLabel && (
											<span className="review-delete-users__user-role">
												{user.RoleLabel}
											</span>
										)}
									</div>
									<span className="review-delete-users__user-email">
										{user.EmailAddress}
									</span>
								</div>
							</div>
						);
					})}
				</div>

				<InfoBanner type="warning">
					<p>
						Selected users will be removed immediately.
						{nextBillingDate && (
							<>
								{' '}
								Billing continues through{' '}
								<strong>
									{(Util as any).formatDateReadable(nextBillingDate)}
								</strong>
								.
							</>
						)}{' '}
						You can add new users again anytime by increasing your seats.
					</p>
				</InfoBanner>
			</div>
		</Modal>
	);
};

export default ReviewDeleteUsersModal;
